// Paywall A/B test: who takes part and which paywall they get. Pure functions,
// shared by the web app, the iOS and Android apps and (as a Python copy) the
// weekly report, scripts/paywall-test/report.py. Rules: docs/paywall-test.md.
//
// Assignment is a hash of the test's salt and the username, so an account
// gets the same paywall on every device and the report can recompute it from
// the username alone. Nothing is stored server-side.

export const VARIANTS = ["tiers", "picker"];
export const CONTROL = "tiers";
export const TEST_VARIANT = "picker";

// 32-bit FNV-1a over the string's UTF-8 bytes. Keep in step with
// scripts/paywall-test/report.py (same test vectors in both test suites).
export function fnv1a32(text) {
  const bytes = utf8(String(text));
  let hash = 0x811c9dc5;
  for (const byte of bytes) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function utf8(text) {
  const out = [];
  for (const char of text) {
    let code = char.codePointAt(0);
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 63));
    else if (code < 0x10000)
      out.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 63),
        0x80 | (code & 63)
      );
    else
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 63),
        0x80 | ((code >> 6) & 63),
        0x80 | (code & 63)
      );
  }
  return out;
}

// 0–9999. Usernames are lower-cased so "Anna" and "anna" never split.
export function bucketFor(salt, username) {
  const name = String(username || "")
    .trim()
    .toLowerCase();
  return fnv1a32(`${salt}:${name}`) % 10000;
}

// share = percent of eligible accounts that get the picker (50 for 50/50).
export function variantFor(config, username) {
  const forced =
    config?.force?.[
      String(username || "")
        .trim()
        .toLowerCase()
    ];
  if (VARIANTS.includes(forced)) return forced;
  const share = Math.max(0, Math.min(100, Number(config?.share) || 0));
  return bucketFor(config?.salt || config?.id, username) < share * 100
    ? TEST_VARIANT
    : CONTROL;
}

export function compareVersions(a, b) {
  const pa = String(a || "0")
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  const pb = String(b || "0")
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d < 0 ? -1 : 1;
  }
  return 0;
}

const parseTime = (value) => {
  const t = Date.parse(value || "");
  return Number.isFinite(t) ? t : NaN;
};

// Who takes part. Anyone not eligible sees today's paywall and is not counted.
//   config:  the test config (components/experiments/paywall-test.json)
//   profile: { username, account_type, legacyPricing, registered_datetime, email }
//   client:  { platform: "web" | "ios" | "android", appVersion }
export function eligibility(config, profile, client) {
  const username = String(profile?.username || "")
    .trim()
    .toLowerCase();
  if (!config || config.status !== "on")
    return { eligible: false, reason: "off" };
  if (!username) return { eligible: false, reason: "no-account" };
  const forced = config.force?.[username];
  if (VARIANTS.includes(forced)) return { eligible: true, reason: "forced" };
  if (profile.account_type === "business")
    return { eligible: false, reason: "business" };
  if (profile.legacyPricing)
    return { eligible: false, reason: "legacy-pricing" };
  if (/@eventlive\.pro$/i.test(String(profile.email || "").trim()))
    return { eligible: false, reason: "internal" };

  // New accounts only, so nobody who saw today's paywall is shown another.
  const start = parseTime(config.start);
  const registered = parseTime(profile.registered_datetime);
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(registered) ||
    registered < start
  )
    return { eligible: false, reason: "registered-before-start" };

  // In the apps, only store builds that already contain both paywalls. An
  // over-the-air update only reaches people from their second launch, after
  // most have already seen (and paid on) the paywall shipped in the build.
  const platform = client?.platform;
  if (platform === "ios" || platform === "android") {
    const min = config.minAppVersion?.[platform];
    if (!min || compareVersions(client?.appVersion, min) < 0)
      return { eligible: false, reason: "app-version" };
  } else if (platform !== "web") {
    return { eligible: false, reason: "platform" };
  }
  if (config.platforms && config.platforms[platform] === false)
    return { eligible: false, reason: "platform-off" };
  return { eligible: true, reason: "eligible" };
}

// The paywall this account sees, plus what to log.
export function assignPaywall(config, profile, client) {
  const { eligible, reason } = eligibility(config, profile, client);
  if (!eligible) return { enrolled: false, variant: CONTROL, reason };
  return {
    enrolled: true,
    variant: variantFor(config, profile.username),
    bucket: bucketFor(config.salt || config.id, profile.username),
    reason,
  };
}

// Preview builds only (Netlify deploy previews and localhost, never the live
// site): ?paywall_test=picker or ?paywall_test=tiers shows that paywall to
// whoever is signed in, whatever the config says; ?paywall_test=off clears it.
// `saved` is the choice kept from earlier in the tab; `save` says what to keep
// ("" = forget it, null = leave as is).
export const PREVIEW_PARAM = "paywall_test";

export function isPreviewHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".netlify.app")
  );
}

export function previewChoice(hostname, search, saved) {
  if (!isPreviewHost(hostname)) return { variant: null, save: null };
  let value = null;
  try {
    value = new URLSearchParams(search || "").get(PREVIEW_PARAM);
  } catch {
    value = null;
  }
  if (VARIANTS.includes(value)) return { variant: value, save: value };
  if (value === "off") return { variant: null, save: "" };
  return { variant: VARIANTS.includes(saved) ? saved : null, save: null };
}

// Accepts the JSON published at /paywall-test.json; anything malformed is off.
export function normalizeConfig(json) {
  if (!json || typeof json !== "object") return null;
  const id = typeof json.id === "string" ? json.id.trim() : "";
  if (!/^[a-z0-9-]{3,60}$/.test(id)) return null;
  const force = {};
  if (json.force && typeof json.force === "object") {
    for (const [name, variant] of Object.entries(json.force)) {
      if (VARIANTS.includes(variant))
        force[name.trim().toLowerCase()] = variant;
    }
  }
  return {
    id,
    status: json.status === "on" ? "on" : "off",
    start: typeof json.start === "string" ? json.start : null,
    salt: typeof json.salt === "string" && json.salt ? json.salt : id,
    share: Math.max(0, Math.min(100, Number(json.share) || 0)),
    minAppVersion:
      json.minAppVersion && typeof json.minAppVersion === "object"
        ? {
            ios:
              typeof json.minAppVersion.ios === "string"
                ? json.minAppVersion.ios
                : null,
            android:
              typeof json.minAppVersion.android === "string"
                ? json.minAppVersion.android
                : null,
          }
        : {},
    platforms:
      json.platforms && typeof json.platforms === "object"
        ? { ...json.platforms }
        : null,
    force,
  };
}
